import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { ComposerPayload } from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";
import { Lightbox } from "./anexos";
import Composer from "./Composer";

const VAZIO: ComposerPayload = { texto: "", imagens: [], anexosMantidos: [] };

interface NovaDemandaModalProps {
  open: boolean;
  onClose: () => void;
  onCriar: (titulo: string, descricao: ComposerPayload) => Promise<void>;
}

export default function NovaDemandaModal({
  open,
  onClose,
  onCriar,
}: NovaDemandaModalProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState<ComposerPayload>(VAZIO);
  const [tocado, setTocado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [ciclo, setCiclo] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTitulo("");
    setDescricao(VAZIO);
    setTocado(false);
    setEnviando(false);
    setCiclo((n) => n + 1);
  }, [open]);

  const tituloInvalido = tocado && !titulo.trim();

  const enviar = async () => {
    setTocado(true);
    if (!titulo.trim() || enviando) return;
    setEnviando(true);
    let criado = false;
    try {
      await onCriar(titulo, descricao);
      criado = true;
    } catch {
      criado = false;
    } finally {
      setEnviando(false);
    }
    if (criado) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={enviando ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: styles.modalPaper }}
    >
      <Box sx={styles.modalCabecalho}>
        <Box sx={styles.modalCabecalhoIcone}>
          <SupportAgentOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={styles.modalCabecalhoTextos}>
          <Typography sx={styles.modalCabecalhoTitulo}>
            Abrir novo chamado
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={enviando}
          sx={styles.modalFechar}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.modalConteudo}>
        <Box>
          <Typography sx={styles.campoLabel}>Título *</Typography>
          <TextField
            fullWidth
            autoFocus
            autoComplete="off"
            placeholder="Resuma o problema em uma frase"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            onBlur={() => setTocado(true)}
            error={tituloInvalido}
            helperText={tituloInvalido ? "Informe um título." : " "}
            disabled={enviando}
          />
        </Box>

        <Box>
          <Typography sx={styles.campoLabel}>Descrição</Typography>
          <Composer
            key={ciclo}
            placeholder="Adicione detalhes sobre o problema"
            dica="Anexe prints ou documentos"
            disabled={enviando}
            onPreview={setPreview}
            onChange={setDescricao}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={styles.modalAcoes}>
        <Button
          color="inherit"
          onClick={onClose}
          disabled={enviando}
          sx={{ textTransform: "none", borderRadius: 5 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={enviar}
          disabled={enviando || !titulo.trim()}
          sx={styles.modalCtaPrimaria}
          startIcon={
            enviando ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          {enviando ? "Enviando…" : "Abrir chamado"}
        </Button>
      </DialogActions>

      <Lightbox src={preview} onClose={() => setPreview(null)} />
    </Dialog>
  );
}
